// Firmark portal — DEV-ONLY mock data + fetch interceptor for the AppShell
// CHROME preview harness (/portal-preview). Split out of ShellPreviewHarness so
// the install can run EAGERLY, before any React provider mounts.
//
// WHY EAGER: the OUTER AuthProvider (App.tsx) fires GET /api/auth/me in a mount
// effect. If a stale es_token is in localStorage, the real api answers 401 and
// the app redirects to /login BEFORE the lazy harness chunk (which used to seed
// the mock) ever loads. main.tsx imports this module statically at the very top,
// so its install side-effect runs at module-evaluation time — synchronously,
// before createRoot().render() and therefore before the AuthProvider's /me call.
//
// SCOPE GUARD: the install is a no-op unless we're actually on /portal-preview,
// so normal dev pages keep the real fetch + real auth untouched.
//
// PROD: every export here is referenced only behind `import.meta.env.DEV`
// (the main.tsx call site is DEV-guarded, and installShellPreviewMock's body is
// internally DEV-guarded), so Vite statically replaces DEV with `false`,
// dead-code-eliminates the body, and tree-shakes this module out of the prod
// bundle entirely. Verified: dist carries no portal-preview/shell-mock code.

import {
  PROJECT_HELP_HAZARD,
  PROJECT_HELP_HAZARD_RECORD,
  PROJECT_HELP_PIPELINE_READY,
  PROJECT_HELP_PIPELINE_RUNNING,
  PROJECT_HELP_PROJECT,
  PROJECT_HELP_SETUP_CONFIRMED,
  PROJECT_HELP_SETUP_EMPTY,
  PROJECT_HELP_SETUP_UPLOADED,
  PROJECT_HELP_SITE,
} from "../project/projectPreviewFixtures";

// ----------------------------------------------------------------- MOCK DATA
// Module-level mutable staff flag the mocked /api/auth/me reads, so the harness
// staff toggle can flip the Admin gate without rebuilding the interceptor.
let mockStaff = false;

export function setMockStaff(value: boolean): void {
  mockStaff = value;
}

export function mockUser() {
  return {
    id: "demo-user",
    email: "jordan.engineer@maplewood.example",
    name: "Jordan Engineer",
    role: "engineer",
    avatarUrl: null,
    createdAt: "2026-01-04T12:00:00Z",
    lastActiveAt: "2026-06-22T18:30:00Z",
    isStaff: mockStaff,
    activeOrg: {
      id: "org-demo",
      name: "Maplewood Structural",
      role: "engineer",
      accountType: "organization" as const,
    },
    // Two memberships so the UserMenu's org switcher renders.
    memberships: [
      {
        orgId: "org-demo",
        name: "Maplewood Structural",
        role: "engineer",
        isOwner: false,
        accountType: "organization" as const,
      },
      {
        orgId: "org-side",
        name: "Side Consulting LLC",
        role: "viewer",
        isOwner: false,
        accountType: "organization" as const,
      },
    ],
  };
}

const MOCK_ORG = {
  name: "Maplewood Structural",
  accentColor: "#16463b",
};

const MOCK_NOTIFICATIONS = [
  {
    id: "n1",
    type: "pipeline",
    text: "Engineering run completed for Maple St. Residence.",
    read: false,
    createdAt: new Date(Date.now() - 1000 * 60 * 8).toISOString(),
  },
  {
    id: "n2",
    type: "review",
    text: "FJ-2 flagged for review — deflection near limit.",
    read: false,
    createdAt: new Date(Date.now() - 1000 * 60 * 90).toISOString(),
  },
  {
    id: "n3",
    type: "project",
    text: "New project “Oak Ridge Addition” created.",
    read: true,
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 26).toISOString(),
  },
];

// ----------------------------------------------------------- FETCH INTERCEPT
function mockJson(body: unknown): Response {
  return new Response(JSON.stringify(body), {
    status: 200,
    headers: { "Content-Type": "application/json" },
  });
}

type ProjectPreviewMode =
  | "setup"
  | "hazard-intake"
  | "hazard-report"
  | "floor-upload"
  | "floor-running"
  | "floor-ready"
  | "locked";

function projectPreviewMode(): ProjectPreviewMode {
  const value = new URL(window.location.href).searchParams.get("fixture");
  return (
    [
      "setup",
      "hazard-intake",
      "hazard-report",
      "floor-upload",
      "floor-running",
      "floor-ready",
      "locked",
    ] as const
  ).includes(value as ProjectPreviewMode)
    ? (value as ProjectPreviewMode)
    : "setup";
}

function projectForMode(mode: ProjectPreviewMode) {
  return mode === "locked"
    ? {
        ...PROJECT_HELP_PROJECT,
        openedInViewerAt: "2026-07-01T12:00:00.000Z",
        revisionCount: 1,
      }
    : PROJECT_HELP_PROJECT;
}

function setupForMode(mode: ProjectPreviewMode) {
  if (mode === "floor-running" || mode === "floor-ready" || mode === "locked") {
    return PROJECT_HELP_SETUP_UPLOADED;
  }
  if (mode === "floor-upload") return PROJECT_HELP_SETUP_CONFIRMED;
  if (mode === "hazard-intake" || mode === "hazard-report") {
    return { ...PROJECT_HELP_SETUP_EMPTY, siteConfirmed: true };
  }
  return PROJECT_HELP_SETUP_EMPTY;
}

function routeMock(path: string, method: string): Response | null {
  // GET /api/auth/me — drives AuthProvider into the authenticated state.
  if (path.endsWith("/api/auth/me")) return mockJson(mockUser());
  // GET /api/organization — TopBar brand + accent.
  if (path.endsWith("/api/organization")) return mockJson(MOCK_ORG);
  // GET /api/notifications — the bell dropdown.
  if (method === "GET" && path.endsWith("/api/notifications")) {
    return mockJson(MOCK_NOTIFICATIONS);
  }
  // POST /api/notifications/mark-all-read  +  PATCH /api/notifications/:id/read
  if (path.endsWith("/api/notifications/mark-all-read")) return mockJson({ ok: true });
  if (method === "PATCH" && /\/api\/notifications\/[^/]+\/read$/.test(path)) {
    return mockJson({ ok: true });
  }

  // Real project surfaces inside the shell preview. The query-string fixture
  // selects a resting state; every response is deterministic and uses the same
  // constants the help prose imports.
  const mode = projectPreviewMode();
  if (method === "GET" && path === `/api/projects/${PROJECT_HELP_PROJECT.id}`) {
    return mockJson(projectForMode(mode));
  }
  if (method === "GET" && path === `/api/projects/${PROJECT_HELP_PROJECT.id}/setup`) {
    return mockJson(setupForMode(mode));
  }
  if (method === "GET" && path === `/api/projects/${PROJECT_HELP_PROJECT.id}/site`) {
    return mockJson(PROJECT_HELP_SITE);
  }
  if (method === "GET" && path === `/api/projects/${PROJECT_HELP_PROJECT.id}/hazard-record`) {
    if (mode === "hazard-intake") {
      return mockJson({
        ...PROJECT_HELP_HAZARD_RECORD,
        generated: false,
        report: null,
        reportSignature: null,
      });
    }
    return mockJson(PROJECT_HELP_HAZARD_RECORD);
  }
  if (method === "GET" && path === `/api/projects/${PROJECT_HELP_PROJECT.id}/input-context`) {
    return mockJson({ data: {} });
  }
  if (method === "GET" && path === `/api/projects/${PROJECT_HELP_PROJECT.id}/profile-binding`) {
    return mockJson({
      profileId: PROJECT_HELP_PROJECT.profileId,
      overrides: {},
    });
  }
  if (method === "GET" && path === `/api/projects/${PROJECT_HELP_PROJECT.id}/pipeline/status`) {
    return mockJson(
      mode === "floor-running"
        ? PROJECT_HELP_PIPELINE_RUNNING
        : mode === "floor-ready" || mode === "locked"
          ? PROJECT_HELP_PIPELINE_READY
          : {
              runId: null,
              status: "idle",
              currentStep: null,
              currentPhase: null,
              startedAt: null,
              stages: [],
              errorMessage: null,
            },
    );
  }
  if (method === "GET" && path.startsWith(`/api/projects/${PROJECT_HELP_PROJECT.id}/hazard-report`)) {
    return mockJson(PROJECT_HELP_HAZARD);
  }
  if (method === "GET" && path === "/api/settings/loading_defaults") {
    return mockJson({ category: "loading_defaults", data: {} });
  }
  if (method === "GET" && path === "/api/design-profiles") return mockJson([]);

  // Preview interactions can exercise optimistic write-through stores without
  // falling through to a live API. Captures themselves do not depend on writes.
  if (
    (method === "PUT" || method === "POST")
    && path.startsWith(`/api/projects/${PROJECT_HELP_PROJECT.id}/`)
  ) {
    return mockJson({});
  }
  return null;
}

// Install the interceptor + seed the token synchronously. Idempotent via a flag
// on window so HMR re-imports (and the harness component re-importing this) don't
// double-wrap. Returns true if the mock is active (now or from a prior call).
const FETCH_FLAG = "__shellPreviewFetchPatched";

export function installShellPreviewMock(): boolean {
  // SCOPE GUARD: only ever patch on the /portal-preview path; normal dev pages
  // must keep the real fetch + real auth. DEV-only (the body is statically dead
  // in prod, so this whole module tree-shakes out).
  if (!import.meta.env.DEV) return false;
  if (typeof window === "undefined") return false;
  if (!window.location.pathname.startsWith("/portal-preview")) return false;

  if ((window as unknown as Record<string, unknown>)[FETCH_FLAG]) return true;
  (window as unknown as Record<string, unknown>)[FETCH_FLAG] = true;

  // Seed a mock token so getToken() is truthy and AuthProvider attempts /me
  // (which we now answer from the mock instead of the real, 401-ing api).
  try {
    localStorage.setItem("es_token", "dev-shell-preview-token");
    // These product stores are synchronous mirrors. Seed the selected preview
    // fixture before React mounts so useProjectSetup/useHazardOverrides read the
    // same state as their mocked GET responses on their very first render.
    // Without this, a pristine browser context briefly constructs the project
    // accordion from EMPTY and keeps the wrong section open after hydration.
    const mode = projectPreviewMode();
    const hazardRecord = mode === "hazard-intake"
      ? {
          ...PROJECT_HELP_HAZARD_RECORD,
          generated: false,
          report: null,
          reportSignature: null,
        }
      : PROJECT_HELP_HAZARD_RECORD;
    localStorage.setItem(
      "es_project_setup",
      JSON.stringify({ [PROJECT_HELP_PROJECT.id]: setupForMode(mode) }),
    );
    localStorage.setItem(
      "es_project_site",
      JSON.stringify({ [PROJECT_HELP_PROJECT.id]: PROJECT_HELP_SITE }),
    );
    localStorage.setItem(
      "es_hazard_overrides",
      JSON.stringify({ [PROJECT_HELP_PROJECT.id]: hazardRecord }),
    );
  } catch {
    /* storage blocked — the inner AuthProvider will fall to unauthenticated */
  }

  const real = window.fetch.bind(window);
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const url =
      typeof input === "string"
        ? input
        : input instanceof URL
          ? input.toString()
          : input.url;
    const method = (init?.method ?? "GET").toUpperCase();
    const path = new URL(url, window.location.origin).pathname;
    const mocked = routeMock(path, method);
    if (mocked) return mocked;
    return real(input, init);
  };
  return true;
}
